import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Store, DollarSign, BrainCircuit, Package, Settings, TrendingUp, Users, AlertCircle, CheckCircle, Clock, ArrowUpRight, ArrowDownRight, Zap, Database, MapPin, Save, Crown, Bot, Sparkles } from 'lucide-react';

// ─── Lazy-loaded map picker — avoids Rollup scope-hoisting crashes ──
const LazyAdminMapPicker = React.lazy(() => import('./AdminMapLocationPicker'));

interface PosStore {
  id: string;
  name: string;
  location: string;
  billingStatus: 'ACTIVE' | 'GRACE_PERIOD' | 'LOCKED';
  apiKey: string;
  phone?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  website?: string;
  email?: string;
  storeCode?: string;
  adminUsername?: string;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  parentStoreId?: number | null;
  shopType?: string;
  isPersonalAssistantMode?: boolean;
  isWhiteLabelEnabled?: boolean;
  customAvatarUrl?: string;
  customAvatarName?: string;
  customDeveloperName?: string;
}

interface TenantManageModalProps {
  store: PosStore;
  onClose: () => void;
  adminToken: string;
}

interface OverviewData {
  currentProfit: number;
  totalSales: number;
  inventoryCount: number;
  monthlyRevenue: number;
  monthlyLunaUsage: {
    inputTokens: number;
    outputTokens: number;
    totalCostUsd: number;
  };
}

interface BillingData {
  currentBalance: number;
  invoices: Array<{
    id: string;
    invoiceRef: string;
    billingPeriod: string;
    totalAmtUsd: number;
    status: 'UNPAID' | 'PAID' | 'WAIVED' | 'DISPUTED';
    dueDate: string;
  }>;
}

interface LunaUsageData {
  records: Array<{
    id: string;
    billingPeriod: string;
    inputTokens: number;
    outputTokens: number;
    totalCostUsd: number;
    source: 'text_chat' | 'voice_session';
    recordedAt: string;
  }>;
  totalCost: number;
}

interface ProductData {
  products: Array<{
    id: string;
    name: string;
    category: string;
    price: number;
    stockCount: number;
    unitType: string;
  }>;
}

const TabButton = ({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: any; label: string }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all ${
      active
        ? 'bg-purple-500/20 border border-purple-500/30 text-purple-300'
        : 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
    }`}
  >
    <Icon size={14} /> {label}
  </button>
);

const formatCurrency = (amount: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
};

const formatNumber = (num: number) => {
  return new Intl.NumberFormat('en-US').format(num);
};

export default function TenantManageModal({ store: initialStore, onClose, adminToken }: TenantManageModalProps) {
  const [store, setStore] = useState(initialStore);
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'luna' | 'products' | 'settings'>('overview');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [lunaUsage, setLunaUsage] = useState<LunaUsageData | null>(null);
  const [products, setProducts] = useState<ProductData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Location editing state ───────────────────────────────────────────────
  const [editLat, setEditLat] = useState(store.latitude != null ? String(store.latitude) : '');
  const [editLng, setEditLng] = useState(store.longitude != null ? String(store.longitude) : '');
  const [editAddress, setEditAddress] = useState(store.address || '');
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationSaveMsg, setLocationSaveMsg] = useState<'saved' | 'error' | null>(null);

  // ── Personal Assistant Mode toggle ────────────────────────────────────
  const [paModeSaving, setPaModeSaving] = useState(false);

  // ── White-Label & Bespoke Avatar States ────────────────────────────────
  const [whiteLabelSaving, setWhiteLabelSaving] = useState(false);
  const [customAvatarUrlInput, setCustomAvatarUrlInput] = useState(store?.customAvatarUrl || '');
  const [customAvatarNameInput, setCustomAvatarNameInput] = useState(store?.customAvatarName || '');
  const [customDeveloperNameInput, setCustomDeveloperNameInput] = useState(store?.customDeveloperName || '');
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [avatarSaveMsg, setAvatarSaveMsg] = useState<'saved' | 'error' | null>(null);

  const handleToggleWhiteLabel = async () => {
    const newValue = !store.isWhiteLabelEnabled;
    setWhiteLabelSaving(true);
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isWhiteLabelEnabled: newValue }),
      });
      if (res.ok) {
        setStore(prev => ({ ...prev, isWhiteLabelEnabled: newValue }));
      } else {
        setStore(prev => ({ ...prev, isWhiteLabelEnabled: !newValue }));
      }
    } catch {
      setStore(prev => ({ ...prev, isWhiteLabelEnabled: !newValue }));
    } finally {
      setWhiteLabelSaving(false);
    }
  };

  const handleSaveBespokeAvatar = async () => {
    setAvatarSaving(true);
    setAvatarSaveMsg(null);
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          customAvatarUrl: customAvatarUrlInput.trim() || null,
          customAvatarName: customAvatarNameInput.trim() || null,
          customDeveloperName: customDeveloperNameInput.trim() || null,
          isWhiteLabelEnabled: true,
        }),
      });
      if (res.ok) {
        setStore(prev => ({ 
          ...prev, 
          customAvatarUrl: customAvatarUrlInput.trim() || undefined,
          customAvatarName: customAvatarNameInput.trim() || undefined,
          customDeveloperName: customDeveloperNameInput.trim() || undefined,
          isWhiteLabelEnabled: true 
        }));
        setAvatarSaveMsg('saved');
        setTimeout(() => setAvatarSaveMsg(null), 2500);
      } else {
        setAvatarSaveMsg('error');
      }
    } catch {
      setAvatarSaveMsg('error');
    } finally {
      setAvatarSaving(false);
    }
  };

  const handleTogglePaMode = async () => {
    const newValue = !store.isPersonalAssistantMode;
    setPaModeSaving(true);
    // Optimistic update
    setStore(prev => ({ ...prev, isPersonalAssistantMode: newValue }));
    try {
      const res = await fetch(`/api/stores/${store.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPersonalAssistantMode: newValue }),
      });
      if (!res.ok) {
        // Rollback on failure
        setStore(prev => ({ ...prev, isPersonalAssistantMode: !newValue }));
      }
    } catch {
      // Rollback on network error
      setStore(prev => ({ ...prev, isPersonalAssistantMode: !newValue }));
    } finally {
      setPaModeSaving(false);
    }
  };

  const handleSaveLocation = async () => {
    const lat = parseFloat(editLat);
    const lng = parseFloat(editLng);
    if (!isFinite(lat) || !isFinite(lng)) return;
    setSavingLocation(true);
    setLocationSaveMsg(null);
    try {
      const body: Record<string, any> = { latitude: lat, longitude: lng };
      if (editAddress.trim()) body.address = editAddress.trim();
      const res = await fetch(`/api/stores/${store.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { setLocationSaveMsg('error'); return; }
      setStore(prev => ({ ...prev, latitude: lat, longitude: lng, address: editAddress.trim() || prev.address }));
      setLocationSaveMsg('saved');
      setTimeout(() => setLocationSaveMsg(null), 2500);
    } catch {
      setLocationSaveMsg('error');
    } finally {
      setSavingLocation(false);
    }
  };

  const authHeader = () => ({ 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [overviewRes, billingRes, lunaRes, productsRes] = await Promise.all([
          fetch(`/api/admin/stores/${store.id}/overview`, { headers: authHeader() }),
          fetch(`/api/admin/stores/${store.id}/billing`, { headers: authHeader() }),
          fetch(`/api/admin/stores/${store.id}/luna-usage`, { headers: authHeader() }),
          fetch(`/api/admin/stores/${store.id}/products`, { headers: authHeader() }),
        ]);

        const [overviewData, billingData, lunaData, productsData] = await Promise.all([
          overviewRes.ok ? overviewRes.json() : null,
          billingRes.ok ? billingRes.json() : null,
          lunaRes.ok ? lunaRes.json() : null,
          productsRes.ok ? productsRes.json() : null,
        ]);

        setOverview(overviewData);
        setBilling(billingData);
        setLunaUsage(lunaData);
        setProducts(productsData);
      } catch (err) {
        setError('Failed to load tenant data');
        console.error('TenantManageModal fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (store?.id) fetchData();
  }, [store?.id, adminToken]);

  const getBillingStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30';
      case 'GRACE_PERIOD': return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'LOCKED': return 'text-red-400 bg-red-500/10 border-red-500/30';
      default: return 'text-slate-400 bg-slate-500/10 border-slate-500/30';
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-[#0a0a0a] border border-purple-500/30 admin-modal rounded-none w-full max-h-[100vh] overflow-hidden shadow-[0_0_50px_rgba(168,85,247,0.2)]"
      >
        {/* Header */}
        <div className="flex items-center justify-between admin-modal-header p-3 border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
              <Store className="text-purple-400" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-white">{store.name}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-xs text-slate-400 font-mono">{store.location || 'No location'}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getBillingStatusColor(store.billingStatus)}`}>
                  {store.billingStatus.replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:border-white/30 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 p-4 border-b border-white/10 overflow-x-auto">
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} icon={TrendingUp} label="Overview" />
          <TabButton active={activeTab === 'billing'} onClick={() => setActiveTab('billing')} icon={DollarSign} label="Billing" />
          <TabButton active={activeTab === 'luna'} onClick={() => setActiveTab('luna')} icon={BrainCircuit} label="Luna AI" />
          <TabButton active={activeTab === 'products'} onClick={() => setActiveTab('products')} icon={Package} label="Products" />
          <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} icon={Settings} label="Settings" />
        </div>

        {/* Content */}
        <div className="admin-modal-body p-3 overflow-y-auto max-h-[calc(100vh-120px)](90vh-200px)]">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <AlertCircle className="text-red-400 mb-4" size={48} />
              <p className="text-red-400 font-bold">{error}</p>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && overview && (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Current Profit</span>
                        <TrendingUp className="text-emerald-400" size={16} />
                      </div>
                      <p className="text-2xl font-black text-white">{formatCurrency(overview.currentProfit)}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Total Sales</span>
                        <DollarSign className="text-cyan-400" size={16} />
                      </div>
                      <p className="text-2xl font-black text-white">{formatCurrency(overview.totalSales)}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Inventory</span>
                        <Package className="text-purple-400" size={16} />
                      </div>
                      <p className="text-2xl font-black text-white">{formatNumber(overview.inventoryCount)}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">Monthly Revenue</span>
                        <ArrowUpRight className="text-green-400" size={16} />
                      </div>
                      <p className="text-2xl font-black text-white">{formatCurrency(overview.monthlyRevenue)}</p>
                    </div>
                  </div>
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Zap className="text-purple-400" size={16} /> Luna AI Usage (This Month)
                    </h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Input Tokens</p>
                        <p className="text-sm font-black text-white">{formatNumber(overview.monthlyLunaUsage.inputTokens)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Output Tokens</p>
                        <p className="text-sm font-black text-white">{formatNumber(overview.monthlyLunaUsage.outputTokens)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400 uppercase tracking-wider">Cost</p>
                        <p className="text-sm font-black text-purple-400">{formatCurrency(overview.monthlyLunaUsage.totalCostUsd)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Tab */}
              {activeTab === 'billing' && billing && (
                <div className="space-y-6">
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-white mb-2">Current Balance</h3>
                    <p className="text-3xl font-black text-white">{formatCurrency(billing.currentBalance)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-4">Invoice History</h3>
                    <div className="space-y-3">
                      {billing.invoices.map(invoice => (
                        <div key={invoice.id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-bold text-white">{invoice.invoiceRef}</p>
                              <p className="text-xs text-slate-400">Period: {invoice.billingPeriod}</p>
                              <p className="text-xs text-slate-400">Due: {new Date(invoice.dueDate).toLocaleDateString()}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-black text-white">{formatCurrency(invoice.totalAmtUsd)}</p>
                              <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                                invoice.status === 'PAID' ? 'bg-emerald-500/10 text-emerald-400' :
                                invoice.status === 'UNPAID' ? 'bg-red-500/10 text-red-400' :
                                'bg-slate-500/10 text-slate-400'
                              }`}>
                                {invoice.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Luna AI Tab */}
              {activeTab === 'luna' && lunaUsage && (
                <div className="space-y-6">
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-white mb-2">Total AI Cost</h3>
                    <p className="text-3xl font-black text-purple-400">{formatCurrency(lunaUsage.totalCost)}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white mb-4">Usage History</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className="text-left py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Period</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Input</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Output</th>
                            <th className="text-right py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Cost</th>
                            <th className="text-left py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lunaUsage.records.map(record => (
                            <tr key={record.id} className="border-b border-white/5">
                              <td className="py-3 px-4 text-white">{record.billingPeriod}</td>
                              <td className="py-3 px-4 text-right text-white">{formatNumber(record.inputTokens)}</td>
                              <td className="py-3 px-4 text-right text-white">{formatNumber(record.outputTokens)}</td>
                              <td className="py-3 px-4 text-right text-purple-400">{formatCurrency(record.totalCostUsd)}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                                  record.source === 'voice_session' ? 'bg-purple-500/10 text-purple-400' : 'bg-cyan-500/10 text-cyan-400'
                                }`}>
                                  {record.source.replace('_', ' ')}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Products Tab */}
              {activeTab === 'products' && products && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      <Package className="text-purple-400" size={16} /> Inventory ({products.products.length} items)
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Product</th>
                          <th className="text-left py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Category</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Price</th>
                          <th className="text-right py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Stock</th>
                          <th className="text-left py-3 px-4 text-slate-400 font-bold uppercase tracking-wider">Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.products.map(product => (
                          <tr key={product.id} className="border-b border-white/5">
                            <td className="py-3 px-4 text-white font-medium">{product.name}</td>
                            <td className="py-3 px-4 text-slate-300">{product.category}</td>
                            <td className="py-3 px-4 text-right text-white">{formatCurrency(product.price)}</td>
                            <td className="py-3 px-4 text-right">
                              <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                                product.stockCount > 10 ? 'bg-emerald-500/10 text-emerald-400' :
                                product.stockCount > 0 ? 'bg-yellow-500/10 text-yellow-400' :
                                'bg-red-500/10 text-red-400'
                              }`}>
                                {formatNumber(product.stockCount)}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-slate-300">{product.unitType}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-white mb-4">Store Information</h3>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Store Name</label>
                        <p className="text-white font-mono">{store.name}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Location</label>
                        <p className="text-white font-mono">{store.location || '—'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Store Code</label>
                        <p className="text-purple-400 font-mono">{store.storeCode || '—'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Admin Username</label>
                        <p className="text-white font-mono">{store.adminUsername || '—'}</p>
                      </div>
                      {store.phone && (
                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Phone</label>
                          <p className="text-white font-mono">{store.phone}</p>
                        </div>
                      )}
                      {store.email && (
                        <div>
                          <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Email</label>
                          <p className="text-white font-mono">{store.email}</p>
                        </div>
                      )}
                      {store.website && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Website</label>
                          <a href={store.website} target="_blank" rel="noreferrer" className="text-cyan-400 font-mono hover:underline">{store.website}</a>
                        </div>
                      )}
                      {/* ── Personal Assistant Mode ── */}
                      <div className="md:col-span-2 border-t border-purple-500/10 pt-4 mt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold text-purple-300 flex items-center gap-2">
                              <BrainCircuit size={14} /> Personal Assistant Mode
                            </h4>
                            <p className="text-xs text-slate-400 mt-1">
                              When enabled, Luna acts as a general-purpose personal assistant without access to store inventory, sales, or employee data.
                            </p>
                          </div>
                          <button
                            onClick={handleTogglePaMode}
                            disabled={paModeSaving}
                            className={`relative w-14 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                              store.isPersonalAssistantMode ? 'bg-purple-500' : 'bg-slate-700'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${
                                store.isPersonalAssistantMode ? 'translate-x-7' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        <p className="text-xs font-mono mt-2 text-slate-500">
                          Current: {store.isPersonalAssistantMode ? 'UNRESTRICTED' : 'STORE MODE'}
                        </p>
                      </div>

                      {/* ── White-Label & Custom Agent Renaming ── */}
                      <div className="md:col-span-2 border-t border-purple-500/10 pt-4 mt-4 bg-gradient-to-r from-amber-500/10 via-slate-900 to-cyan-500/10 p-5 rounded-2xl border border-amber-500/30 shadow-xl">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-bold text-amber-300 flex items-center gap-2">
                              <Crown size={16} className="text-amber-400" /> Premium White-Label & Custom Agent Renaming
                            </h4>
                            <p className="text-xs text-slate-400 mt-1">
                              Allow this tenant to change their Agent Name from "Luna AI" to their custom brand name.
                            </p>
                          </div>
                          <button
                            onClick={handleToggleWhiteLabel}
                            disabled={whiteLabelSaving}
                            className={`relative w-14 h-7 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50 ${
                              store.isWhiteLabelEnabled ? 'bg-amber-500' : 'bg-slate-700'
                            }`}
                          >
                            <span
                              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${
                                store.isWhiteLabelEnabled ? 'translate-x-7' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                        <p className="text-xs font-mono mt-2 text-slate-400">
                          Status: <span className={store.isWhiteLabelEnabled ? 'text-emerald-400 font-bold' : 'text-amber-400 font-bold'}>
                            {store.isWhiteLabelEnabled ? '✨ UNLOCKED (Tenant Can Rename Agent)' : '🔒 LOCKED (Free Tier Default)'}
                          </span>
                        </p>

                        {/* Bespoke 3D Avatar Settings */}
                        <div className="mt-4 pt-4 border-t border-amber-500/20 space-y-3">
                          <h5 className="text-xs font-bold text-cyan-300 flex items-center gap-1.5 uppercase tracking-wider">
                            <Bot size={14} className="text-cyan-400" /> Bespoke 3D WebGL Avatar (GLB Model / Face)
                          </h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase tracking-wider">Avatar Display Name</label>
                              <input
                                type="text"
                                value={customAvatarNameInput}
                                onChange={(e) => setCustomAvatarNameInput(e.target.value)}
                                placeholder="e.g. Cyber Bot Mascot"
                                className="w-full px-3 py-2 bg-slate-950/80 border border-cyan-500/30 rounded-xl text-white font-mono text-xs focus:outline-none focus:border-cyan-400 transition-colors"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold text-slate-400 mb-1 uppercase tracking-wider">Custom 3D Model (.glb URL)</label>
                              <input
                                type="text"
                                value={customAvatarUrlInput}
                                onChange={(e) => setCustomAvatarUrlInput(e.target.value)}
                                placeholder="https://.../model.glb"
                                className="w-full px-3 py-2 bg-slate-950/80 border border-cyan-500/30 rounded-xl text-cyan-300 font-mono text-xs focus:outline-none focus:border-cyan-400 transition-colors"
                              />
                            </div>
                          </div>
                          {/* Enterprise Tier: Custom Developer & Platform Attribution */}
                          <div className="pt-3 border-t border-amber-500/20 space-y-1">
                            <label className="block text-[10px] font-bold text-amber-300 uppercase tracking-wider flex items-center justify-between">
                              <span>👑 Enterprise Tier: Developer / Platform Attribution Rebrand</span>
                              <span className="text-[9px] text-slate-400 font-normal">High-Tier Paid Option</span>
                            </label>
                            <input
                              type="text"
                              value={customDeveloperNameInput}
                              onChange={(e) => setCustomDeveloperNameInput(e.target.value)}
                              placeholder="e.g. DTech Technologies & Robotics (leave empty for Kangavena default)"
                              className="w-full px-3 py-2 bg-slate-950/80 border border-amber-500/30 rounded-xl text-amber-200 font-mono text-xs focus:outline-none focus:border-amber-400 transition-colors"
                            />
                            <p className="text-[10px] text-slate-400 font-mono">
                              If empty, default attribution is enforced: <strong className="text-slate-300">Created by Kangavena Cloud Platform (කඟවේනා - kangavena.com)</strong>.
                            </p>
                          </div>

                          <div className="flex items-center gap-3 pt-1">
                            <button
                              onClick={handleSaveBespokeAvatar}
                              disabled={avatarSaving}
                              className="px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-amber-500/20 hover:from-cyan-500/30 hover:to-amber-500/30 text-cyan-200 rounded-xl text-xs font-bold uppercase tracking-wider border border-cyan-500/40 transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                            >
                              <Save size={13} />
                              {avatarSaving ? 'Saving...' : 'Save Branding & Unlock'}
                            </button>
                            {avatarSaveMsg === 'saved' && <span className="text-xs text-emerald-400 font-mono">✅ Saved & Unlocked!</span>}
                            {avatarSaveMsg === 'error' && <span className="text-xs text-rose-400 font-mono">❌ Failed to save</span>}
                          </div>
                        </div>
                      </div>
                      {/* ── Editable Address ── */}
                      {store.address !== undefined && (
                        <div className="md:col-span-2">
                          <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Address</label>
                          <input
                            type="text"
                            value={editAddress}
                            onChange={(e) => setEditAddress(e.target.value)}
                            placeholder="Store street address"
                            className="w-full px-3 py-2 bg-slate-900/70 border border-purple-500/20 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500/50 transition-colors"
                          />
                        </div>
                      )}
                      {/* ── Editable Location with Map Picker ── */}
                      <div className="md:col-span-2 border-t border-purple-500/10 pt-4 mt-2">
                        <h4 className="text-sm font-bold text-purple-300 mb-3 flex items-center gap-2">
                          <MapPin size={14} /> Store Location Coordinates
                        </h4>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Latitude</label>
                            <input
                              type="text"
                              value={editLat}
                              onChange={(e) => setEditLat(e.target.value)}
                              placeholder="7.8731"
                              className="w-full px-3 py-2 bg-slate-900/70 border border-purple-500/20 rounded-lg text-cyan-300 font-mono text-sm focus:outline-none focus:border-purple-500/50 transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-slate-400 mb-1 uppercase tracking-wider">Longitude</label>
                            <input
                              type="text"
                              value={editLng}
                              onChange={(e) => setEditLng(e.target.value)}
                              placeholder="80.7718"
                              className="w-full px-3 py-2 bg-slate-900/70 border border-purple-500/20 rounded-lg text-cyan-300 font-mono text-sm focus:outline-none focus:border-purple-500/50 transition-colors"
                            />
                          </div>
                        </div>

                        <div className="relative">
                          <React.Suspense
                            fallback={
                              <div className="h-52 w-full rounded-xl bg-slate-800/30 border border-purple-500/20 flex items-center justify-center">
                                <span className="text-xs text-slate-500 font-mono">Loading map…</span>
                              </div>
                            }
                          >
                            <LazyAdminMapPicker
                              lat={editLat}
                              lng={editLng}
                              onChange={(lat, lng) => { setEditLat(lat); setEditLng(lng); }}
                            />
                          </React.Suspense>
                        </div>

                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={handleSaveLocation}
                            disabled={savingLocation || !editLat || !editLng}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded-lg text-xs font-bold uppercase tracking-wider border border-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Save size={14} />
                            {savingLocation ? 'Saving…' : 'Save Location'}
                          </button>
                          {locationSaveMsg === 'saved' && (
                            <span className="flex items-center gap-1 text-emerald-400 text-xs font-bold">
                              <CheckCircle size={14} /> Coordinates updated
                            </span>
                          )}
                          {locationSaveMsg === 'error' && (
                            <span className="flex items-center gap-1 text-red-400 text-xs font-bold">
                              <AlertCircle size={14} /> Failed to save
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                    <h3 className="text-sm font-bold text-amber-400 mb-2 flex items-center gap-2">
                      <AlertCircle size={16} /> Credential Management
                    </h3>
                    <p className="text-xs text-slate-400 mb-4">
                      To reset store credentials, use the 'Reset Credentials' button from the main store list. This ensures proper audit trails and security.
                    </p>
                    <button
                      onClick={() => {
                        alert('Please use the "Reset Credentials" button from the main store list for security and audit purposes.');
                      }}
                      className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg font-bold text-xs uppercase tracking-wider border border-amber-500/30 transition-all"
                    >
                      Go to Credential Reset
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
